import { useCallback, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'tyro:documentation:recentlyViewed';
const MAX_ENTRIES = 10;

export interface RecentlyViewedDoc {
  pageId: number;
  pageSlug: string;
  title: string;
  entityType: string;
  spaceId: number;
  spaceSlug: string;
  spaceName: string;
  viewedAt: number;
}

function readStorage(): RecentlyViewedDoc[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (entry): entry is Partial<RecentlyViewedDoc> =>
          entry != null &&
          typeof entry.pageId === 'number' &&
          typeof entry.pageSlug === 'string' &&
          typeof entry.title === 'string' &&
          typeof entry.spaceId === 'number' &&
          typeof entry.spaceSlug === 'string' &&
          typeof entry.spaceName === 'string' &&
          typeof entry.viewedAt === 'number',
      )
      .map(
        (entry): RecentlyViewedDoc => ({
          pageId: entry.pageId as number,
          pageSlug: entry.pageSlug as string,
          title: entry.title as string,
          entityType:
            typeof entry.entityType === 'string' ? entry.entityType : 'article',
          spaceId: entry.spaceId as number,
          spaceSlug: entry.spaceSlug as string,
          spaceName: entry.spaceName as string,
          viewedAt: entry.viewedAt as number,
        }),
      );
  } catch {
    return [];
  }
}

function writeStorage(entries: RecentlyViewedDoc[]) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // storage unavailable — silently ignore
  }
}

let cache: RecentlyViewedDoc[] = readStorage();
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function setCache(next: RecentlyViewedDoc[]) {
  cache = next;
  writeStorage(next);
  emit();
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    cache = readStorage();
    emit();
  });
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): RecentlyViewedDoc[] {
  return cache;
}

function getServerSnapshot(): RecentlyViewedDoc[] {
  return [];
}

export function useRecentlyViewedDocs() {
  const entries = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );

  const recordView = useCallback((doc: Omit<RecentlyViewedDoc, 'viewedAt'>) => {
    const next = [
      { ...doc, viewedAt: Date.now() },
      ...cache.filter((entry) => entry.pageId !== doc.pageId),
    ].slice(0, MAX_ENTRIES);
    setCache(next);
  }, []);

  const clear = useCallback(() => {
    setCache([]);
  }, []);

  return { entries, recordView, clear };
}
